// src/pages/ExperienceCatalog.tsx

import { RiArrowLeftSLine } from 'react-icons/ri';
import { Link, useNavigate } from 'react-router-dom';

import heroPhone from 'assets/img/experienceCatalog/hero-phone.png';
import idea from 'assets/img/experienceCatalog/idea.png';
import qualityLife from 'assets/img/experienceCatalog/quality-of-life.png';
import smiley from 'assets/img/experienceCatalog/smiley.png';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import { ExperienceCatalogResponse, PremiacaoIsolada, Combo } from 'types/experienceCatalog';
import {
  fetchExperienceCatalogList,
  getDeadLineExperienceCatalog,
  purchaseItemExperienceCatalog,
  loadExperiencePurchase,
  getRegulationExperienceCatalog,
} from 'services/experienceCataolog.service';
import ExperienceSelectCard from './ExperienceSelectCard';
import { GENERIC_REFUSAL_MESSAGE, PurchaseOutcome, redeemExperience } from './purchaseOutcome';
import { useAuth } from 'context/AuthContext';
import { Countdown } from 'components/countdown';
import ProgressBar from 'components/ui/progress-bar';
import TabTitle from 'components/tab-title';
import { getNamedDates } from 'services/utils';
import { getServerTime } from 'services/dashboard.service';
import { canAccessExperienceCatalog } from 'utils/experience-catalog';
import { readCatalogPeriod, resolveCatalogPeriodState } from 'utils/catalog-period';

const purchaseRestrictionMessages: Record<string, string> = {
  insufficient_requirements: 'Estrelas insuficientes para realizar o resgate.',
  exceeded_purchase_limit: 'Limite de resgate da experiência atingido.',
  item_does_not_exist: 'Esta experiência não existe mais.',
  player_does_not_exist: 'O player informado para o resgate não existe.',
  item_disabled: 'Esta experiência está desabilitada.',
  item_out_of_time: 'Ultrapassado o período para resgate da experiência.',
  limit_exceeded: 'Você já realizou o resgate desta experiência.',
  item_sold_out: 'Esta experiência está esgotada.',
  principal_not_allowed: 'Você não tem permissão para realizar o resgate.',
  // Recusa da trigger `FnEN39m` quando a janela do ciclo não está aberta. O código é o mesmo
  // string gravado lá; sem esta linha a recusa cairia na mensagem genérica de erro.
  catalog_period_closed: 'O período de resgate deste ciclo não está aberto.',
};

export default function ExperienceCatalog() {
  const { userInfo, realCycle, cycleSelected } = useAuth();
  const [catalogData, setCatalogData] = useState<ExperienceCatalogResponse>();
  const [deadLine, setDeadLine] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [loading, setLoading] = useState(true);
  const [ruleUrl, setRuleUrl] = useState('');
  const [optionsCycle, setOptionsCycle] = useState<any[]>([]);
  const [activeCycleObj, setActiveCycleObj] = useState<any>();
  const [realDatetime, setRealDatetime] = useState<number>(Date.now());
  const [hasTime, setHasTime] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const aguardandoAbertura = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    let interv: any = undefined;

    getServerTime().then(timestamp => {
      setRealDatetime(timestamp);
      setHasTime(true);

      interv = window.setInterval(() => {
        setRealDatetime(prevstate => prevstate + 1000);
      }, 1000);
    });

    return () => clearInterval(interv);
  }, []);

  const verifyExperience = useCallback(async () => {
    try {
      if (!userInfo || !userInfo.chave || !activeCycleObj?.title) {
        return;
      }
      const response = await loadExperiencePurchase(userInfo.chave, activeCycleObj?.title);
      if (response) {
        navigate('feedback');
      }
    } catch (error) {
      console.error('Error verifying experience:', error);
    }
  }, [userInfo, activeCycleObj, navigate]);

  useEffect(() => {
    if (!realCycle) return;

    const minCycleDate = getNamedDates().ciclo_1S2025;
    const maxCycleDate = getNamedDates().ciclo_1S2026;
    const cycleOptions = realCycle
      .filter((item: any) => item.date_start >= minCycleDate && item.date_start <= maxCycleDate)
      .map((cycle: any) => {
        cycle.label = 'Ciclo ' + cycle.title;
        cycle.value = cycle._id;
        return cycle;
      });

    setOptionsCycle(cycleOptions);
    if (!activeCycleObj && cycleOptions.length > 0) {
      setActiveCycleObj(cycleOptions.find(e => e._id === cycleSelected?._id) || cycleOptions[0]);
    }
  }, [activeCycleObj, cycleSelected, realCycle]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo || !userInfo.chave || !canAccessExperienceCatalog(userInfo)) {
        setLoading(false);
        return;
      }
      if (!activeCycleObj) {
        return;
      }
      setLoading(true);
      try {
        const result = await fetchExperienceCatalogList(userInfo.chave, activeCycleObj?.title);
        const deadLineResponse = await getDeadLineExperienceCatalog(activeCycleObj?._id);
        const rule = await getRegulationExperienceCatalog(activeCycleObj?.title);

        if (rule) {
          setRuleUrl(rule.file?.url || '');
        }

        // As duas datas saem do documento por `readCatalogPeriod`, que lê `startDate` e `endDate` e
        // mais nada. A leitura anterior aceitava `created` como segunda opção para a abertura: são
        // campos diferentes (no 1S2026, 14/08 e 13/08), e enquanto o `startDate` só alimentava a
        // barra de progresso a troca passava batida. Agora ela é a data que o jogador lê no relógio.
        const periodo = readCatalogPeriod(deadLineResponse);

        // O diagnóstico de campo ilegível continua no console, como antes desta feature. O que
        // saiu foi o FALLBACK: `endDate` inválido virava "agora", e o efeito logo abaixo
        // redirecionava o jogador para a página de feedback como se o prazo tivesse encerrado.
        // Chutar uma data é pior do que não ter data — sem `endDate` a página fica sem contagem,
        // e ninguém é expulso por causa de um documento mal cadastrado.
        if (!periodo.end) {
          console.error('endDate ausente ou ilegível no período do ciclo:', deadLineResponse?.endDate);
        }

        if (!periodo.start) {
          console.error('startDate ausente ou ilegível no período do ciclo:', deadLineResponse?.startDate);
        }

        setDeadLine(periodo.end);
        setStartDate(periodo.start);

        setCatalogData(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    verifyExperience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, activeCycleObj, verifyExperience, reloadToken]);

  /*
   * Onde o relógio do SERVIDOR cai na janela do ciclo. `getServerTime()` e não `Date.now()`: um
   * navegador atrasado abriria a vitrine antes da hora, e um adiantado esconderia o catálogo
   * aberto. Sem a hora do servidor ainda carregada, nada é decidido.
   */
  const periodState = useMemo(
    () => (hasTime ? resolveCatalogPeriodState(realDatetime, { start: startDate, end: deadLine }) : 'desconhecido'),
    [hasTime, realDatetime, startDate, deadLine],
  );

  /*
   * VIG-11 — quando a contagem chega a zero, a página busca o catálogo de novo sozinha.
   *
   * A recarga acontece só na TRANSIÇÃO de "antes" para "aberto", guardada pela ref: `periodState`
   * é recalculado a cada segundo, e disparar pelo estado atual recarregaria a página para sempre.
   * Quem já chega com a janela aberta não recarrega nada.
   */
  useEffect(() => {
    if (periodState === 'antes') {
      aguardandoAbertura.current = true;
      return;
    }

    if (periodState === 'aberto' && aguardandoAbertura.current) {
      aguardandoAbertura.current = false;
      setReloadToken(token => token + 1);
    }
  }, [periodState]);

  useEffect(() => {
    if (deadLine && hasTime && realDatetime > 0) {
      if (new Date(realDatetime) > new Date(deadLine)) {
        navigate('feedback');
      }
    }
  }, [realDatetime, deadLine, hasTime, navigate]);

  const handleTabChange = (cycle: any) => {
    setActiveCycleObj(cycle);
  };

  /*
   * O resultado volta para o modal do card, que mostra o painel de cada caso. A página entra só
   * com o que é dela: ir para o feedback quando o resgate gravou e, quando a confirmação não
   * chegou, conferir se ele gravou mesmo assim (`verifyExperience` leva ao feedback se existir).
   */
  const purchaseExperience = async (item: PremiacaoIsolada | Combo): Promise<PurchaseOutcome> => {
    const playerId = userInfo?.chave;
    if (!playerId) {
      return { kind: 'recusada', message: GENERIC_REFUSAL_MESSAGE };
    }

    return redeemExperience({
      purchase: () => purchaseItemExperienceCatalog(item, playerId),
      restrictionMessages: purchaseRestrictionMessages,
      onConfirmed: () => navigate('feedback'),
      onUnconfirmed: verifyExperience,
    });
  };

  return (
    <div className="text-white min-h-screen">
      {/* Skip Link para navegação rápida */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-yellow text-oxford-blue px-4 py-2 rounded-md font-bold z-50 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
      >
        Pular para conteúdo principal
      </a>

      {loading ? (
        <div
          className="text-center text-white flex flex-col items-center justify-center min-h-screen"
          role="status"
          aria-live="polite"
          aria-label="Carregando catálogo de experiências"
        >
          <ArticlePlaceholder />
          <ArticlePlaceholder />
          <span className="sr-only">Carregando informações do catálogo de experiências...</span>
        </div>
      ) : (
        <>
          <header className="flex flex-col items-start py-10 container mx-auto lg:p-10 mb-20">
            <div className="flex flex-row mb-4 justify-between items-center w-full">
              <div className="flex flex-row items-center gap-4">
                <Link to="../" className="focus:outline-none">
                  <button
                    className="rounded-full bg-purple flex justify-center items-center p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-purple transition-all"
                    aria-label="Voltar para a página anterior"
                  >
                    <RiArrowLeftSLine size={28} className="text-white" />
                  </button>
                </Link>
                <h1
                  className="text-lg lg:text-3xl py-1 px-6 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                  tabIndex={0}
                  role="banner"
                  aria-label="Página principal: Catálogos de Experiências"
                >
                  Catálogos de Experiências
                </h1>
              </div>
              {optionsCycle?.length > 0 && (
                <div className="z-50 relative">
                  <TabTitle
                    options={optionsCycle}
                    title={<p>Selecione o ciclo do catálogo:</p>}
                    onCallBack={handleTabChange}
                    hideLabel={true}
                  />
                </div>
              )}
            </div>
          </header>

          <section aria-labelledby="hero-heading">
            <div className="bg-maastricht-blue flex flex-col md:flex-row items-center py-10 px-4">
              <div className="flex flex-row items-center container mx-auto">
                <div className="lg:px-10">
                  <h2
                    id="hero-heading"
                    className="text-4xl font-semibold text-electric-blue mb-4 max-w-2xl relative focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-blue focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                  >
                    Viva o que importa!
                  </h2>
                  <p
                    className="max-w-2xl text-2xl text-center md:text-left mb-6 font-extralight focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                    aria-label="Descrição do Catálogo de Experiências: O Catálogo de Experiências é um convite para criar memórias e escolher momentos que fazem sentido para você. Seja para celebrar, presentear ou viver algo novo."
                  >
                    O <b>Catálogo de Experiências</b> é um convite pra criar memórias e escolher momentos que fazem sentido pra
                    você. Seja para celebrar, presentear ou viver algo novo. Acesse o regulamento do{' '}
                    <a
                      className="font-bold underline hover:no-underline focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-1 focus-visible:outline-none rounded-sm py-1 transition-all"
                      href={ruleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Abrir regulamento em nova aba (abre em nova janela)"
                    >
                      Catálogo de Experiências aqui
                    </a>
                  </p>
                </div>
                <img
                  src={heroPhone}
                  alt="Telefone celular mostrando a interface do aplicativo do Catálogo de Experiências com opções de experiências disponíveis"
                  className="w-[300px] md:w-[520px] lg:w-[600px] right-[0%] 2xl:right-[17%] absolute hidden md:block"
                  aria-describedby="hero-phone-desc"
                />
                <span id="hero-phone-desc" className="sr-only">
                  Imagem ilustrativa mostrando um smartphone com a tela do aplicativo do Catálogo de Experiências, demonstrando as
                  funcionalidades disponíveis para os usuários
                </span>
              </div>
            </div>
          </section>

          <section className="bg-slate-blue py-10 px-4" aria-labelledby="benefits-heading">
            <div className="flex flex-col items-start justify-start container mx-auto lg:px-10">
              <h3
                id="benefits-heading"
                className="text-4xl font-bold mb-14 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                tabIndex={0}
              >
                Porque a vida é feita
                <br /> de experiências!
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto text-xl font-extralight">
                <div
                  aria-label="Benefício 1: Experiências enriquecem a vida e significam investir em si mesmo."
                  className="flex flex-col items-center border-r-2 border-white pr-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                >
                  <img
                    src={qualityLife}
                    alt="Ícone de qualidade de vida mostrando uma pessoa em posição de meditação, representando bem-estar e autocuidado"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">Experiências enriquecem a vida e significam investir em si mesmo.</p>
                </div>
                <div
                  className="flex flex-col items-center border-r-2 border-white pr-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                >
                  <img
                    src={idea}
                    alt="Ícone de inspiração mostrando uma lâmpada acesa, simbolizando ideias e criatividade"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">Inspiram novas formas de ver o mundo e viver a vida.</p>
                </div>
                <div
                  className="flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-lg p-4"
                  tabIndex={0}
                  aria-label="Benefício 3: Experiências criam memórias duradouras e expandem os horizontes."
                >
                  <img
                    src={smiley}
                    alt="Ícone de felicidade mostrando um emoji sorridente, representando alegria e satisfação"
                    className="w-[80px] mb-4"
                  />
                  <p className="text-center font-medium">
                    Criam memórias <br />
                    duradouras e expandem os horizontes.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {periodState === 'antes' && startDate && (
            <section className="container mx-auto pt-8 pb-3 px-10" aria-labelledby="opening-section">
              <div className="relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] px-6 lg:px-8 py-8 flex flex-col space-y-4 2xl:mr-[152px]">
                <p id="opening-section" className="text-center mb-2 flex justify-center text-white font-medium">
                  O Catálogo de Experiências abre em:{' '}
                  {/* Sem `aria-label` aqui de propósito: o nome acessível de um relógio é o próprio
                      valor. Um rótulo sobre este span trocaria "12d 03:14:22" por uma frase, e o
                      leitor de tela deixaria de anunciar o tempo. Também sem `aria-live` — um
                      contador que muda a cada segundo transforma a região viva em ruído contínuo.
                      Quem precisa do dado tem a data absoluta logo abaixo, em texto. */}
                  <span className="text-yellow flex font-bold ml-2" role="timer">
                    <Countdown deadline={startDate} />
                  </span>
                </p>
                <p className="text-center text-sm font-light">
                  A escolha das experiências começa em{' '}
                  {startDate.toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  .
                </p>
              </div>
            </section>
          )}

          {periodState !== 'antes' && (
            <section className="container mx-auto pt-8 pb-3 px-10" aria-labelledby="deadline-section">
              <div
                className="relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] px-6 lg:px-8 py-8 flex flex-col space-y-4 2xl:mr-[152px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                tabIndex={0}
              >
                <p
                  id="deadline-section"
                  className="text-center mb-2 flex focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all text-white font-medium"
                  tabIndex={-1}
                >
                  A sua escolha deve ser finalizada em:{' '}
                  <span
                    className="text-yellow flex font-bold ml-2"
                    role="timer"
                    aria-label="Contador de tempo restante para escolha da experiência"
                  >
                    {deadLine && !isNaN(deadLine.getTime()) ? (
                      <>
                        <Countdown deadline={deadLine} />
                      </>
                    ) : (
                      <span aria-label="Carregando informação do prazo">Carregando...</span>
                    )}
                  </span>
                </p>
                <div className="mt-2">
                  <div role="progressbar" aria-label="Progresso do tempo restante para escolha da experiência" tabIndex={-1}>
                    <ProgressBar startDate={startDate?.getTime()} endDate={deadLine?.getTime()} />
                  </div>
                  <p
                    className="text-xs mt-2 font-light focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all"
                    tabIndex={-1}
                    role="note"
                  >
                    Encerrado o prazo de escolha, a concessão de pontos Livelo será realizada automaticamente para quem não tiver
                    registrado uma opção.
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* O aviso de impedimento fala do crédito de uma premiação que ainda não pode ser
              escolhida: antes da abertura ele some junto com a vitrine e com o bloco de ausência
              de itens, para a página não misturar regra de pagamento com "abre em X dias". */}
          {periodState !== 'antes' && activeCycleObj?.date_start >= getNamedDates().ciclo_2S2025 && (
            <section className="container mx-auto pt-5 pb-7 px-10" aria-labelledby="deadline-section">
              <div className=" px-7 lg:flex border-electric-blue border-2 rounded-2xl bg-oxford-blue py-5 2xl:mr-[152px] ">
                <div className={`'w-full -my-5 py-5 pr-0 lg:pr-[70px] pl-3'}`}>
                  <p className="text-lg text-yellow" tabIndex={0}>
                    O crédito da premiação está condicionado a verificação de impedimentos, conforme IN 403-1, item 1.6. A
                    verificação do impedimento terá como base a situação apurada no dia anterior ao pagamento do ciclo.
                  </p>
                </div>
              </div>
            </section>
          )}

          {catalogData?.premiacaoIsolada || catalogData?.combos ? (
            <main id="main-content" className="container px-10 mx-auto pb-12" role="main" aria-labelledby="catalog-heading">
              <h2 id="catalog-heading" className="sr-only">
                Experiências disponíveis para seleção
              </h2>
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 2xl:mr-36"
                role="list"
                aria-label={`${(catalogData?.premiacaoIsolada?.length || 0) + (catalogData?.combos?.length || 0)} experiências disponíveis`}
              >
                {/* Premiações Isoladas */}
                {catalogData?.premiacaoIsolada
                  ?.slice()
                  .sort((a, b) => (a.type === 'pontos_Livelo' ? -1 : b.type === 'pontos_Livelo' ? 1 : 0))
                  .map((item, index) => (
                    <div key={item.itemIdPurchase} role="listitem">
                      <ExperienceSelectCard
                        item={item}
                        validity={deadLine || new Date()}
                        type="único"
                        purchaseExperience={purchaseExperience}
                        ruleUrl={ruleUrl}
                      />
                    </div>
                  ))}

                {/* Combos */}
                {catalogData?.combos?.map((item, index) => (
                  <div key={item.itemIdPurchase} role="listitem">
                    <ExperienceSelectCard
                      item={item}
                      validity={deadLine || new Date()}
                      type="combo"
                      purchaseExperience={purchaseExperience}
                      ruleUrl={ruleUrl}
                    />
                  </div>
                ))}
              </div>
            </main>
          ) : (
            periodState !== 'antes' && (
              <section className="container px-10 mx-auto pb-12" role="alert" aria-labelledby="no-catalog-heading">
                <div
                  className="relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] px-6 lg:px-8 py-8 flex flex-col space-y-4 2xl:mr-[152px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                  tabIndex={0}
                >
                  <h2 id="no-catalog-heading" className="sr-only">
                    Informações sobre acesso ao catálogo
                  </h2>
                  <p
                    className="text-center mb-2 text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue transition-all"
                    role="status"
                    aria-live="polite"
                    tabIndex={0}
                  >
                    {/* Este bloco cobre mais de um motivo — período fechado e pontuação insuficiente
                      chegam aqui do mesmo jeito, porque a recusa da trigger produz o mesmo documento
                      sem itens. O texto anterior afirmava um único motivo ("10.000 pontos") e culpava
                      o jogador por uma porta que às vezes não era dele. */}
                    Colega, não há experiências disponíveis para você neste ciclo no momento. O período de escolha pode ainda não
                    ter começado, ou você pode não ter atingido a pontuação mínima do ciclo. Fique tranquilo: você receberá seus
                    pontos Livelo normalmente, desde que cumpridos os requisitos previstos na IN 403.
                  </p>
                </div>
              </section>
            )
          )}
        </>
      )}
    </div>
  );
}
